/**
 * SinglePageHeader - Reusable header component for single/detail pages
 *
 * Provides consistent header layout with back button, title, subtitle, badges, and actions.
 * Primary actions sit inline with the title (top-right); badges and subtitle appear below.
 *
 * @example
 * ```tsx
 * <SinglePageHeader
 *   title={season.name || `Season #${season.seasonId}`}
 *   backHref="/admin/dashboard/seasons"
 *   backLabel="Back to Seasons"
 *   subtitle="Spring 2026 · Main Studio"
 *   badges={[
 *     <DashboardBadge tone={season.isActive ? "success" : "neutral"}>
 *       {season.isActive ? "Active" : "Inactive"}
 *     </DashboardBadge>
 *   ]}
 *   actions={
 *     <Button variant="primary" leftIcon={<HiOutlinePencil />} onClick={onEdit}>
 *       Edit Season
 *     </Button>
 *   }
 * />
 * ```
 */

"use client";

import { Box, Flex } from "@chakra-ui/react";
import type { ReactNode } from "react";
import { Heading, Text } from "@/components";
import { useThemeTokens } from "@/lib/theme/tokens";
import { SinglePageBackButton } from "./SinglePageBackButton";
import { withPageHeaderActionStyles } from "./pageHeaderActionStyles";

export interface SinglePageHeaderProps {
  /**
   * Page title
   */
  title: string;
  /**
   * Back button href
   */
  backHref?: string;
  /**
   * Back button label
   */
  backLabel?: string;
  /**
   * Content rendered directly below the title row (before badges).
   */
  belowTitle?: ReactNode;
  /**
   * Subtitle text or ReactNode (e.g., contextual location · season line)
   */
  subtitle?: string | ReactNode;
  /**
   * Badges to display (e.g., status badges)
   */
  badges?: ReactNode[];
  /**
   * Action buttons (typically Edit button and actions menu)
   */
  actions?: ReactNode;
  /**
   * Mobile-only: action aligned with the back button (top-right).
   * When set, `actions` is hidden below `md` and `mobileBelowBadgesAction` can follow badges.
   */
  mobileTopAction?: ReactNode;
  /**
   * Mobile-only: action row below badges (e.g. full-width actions menu).
   */
  mobileBelowBadgesAction?: ReactNode;
  /**
   * Additional content to display below title
   */
  children?: ReactNode;
}

export const SinglePageHeader = ({
  title,
  backHref,
  backLabel,
  belowTitle,
  subtitle,
  badges = [],
  actions,
  mobileTopAction,
  mobileBelowBadgesAction,
  children,
}: SinglePageHeaderProps) => {
  const theme = useThemeTokens();
  const hasMobileSplit = Boolean(mobileTopAction);
  const hasMetaBelowTitle =
    badges.length > 0 ||
    Boolean(belowTitle) ||
    Boolean(subtitle) ||
    Boolean(children) ||
    (hasMobileSplit && Boolean(mobileBelowBadgesAction));

  return (
    <Box>
      {backHref && backLabel ? (
        <Flex align="center" justify="space-between" gap={3} mb={3}>
          <SinglePageBackButton href={backHref} label={backLabel} />
          {mobileTopAction ? (
            <Box display={{ base: "block", md: "none" }} flexShrink={0}>
              {withPageHeaderActionStyles(mobileTopAction)}
            </Box>
          ) : null}
        </Flex>
      ) : null}
      <Flex
        gap={3}
        align={{ base: "stretch", md: "flex-start" }}
        justifyContent="space-between"
        direction={{ base: "column", md: "row" }}
        mb={hasMetaBelowTitle ? 2 : 0}
      >
        <Heading
          flex={1}
          minW={0}
          w="100%"
          fontSize={{ base: "xl", md: "3xl" }}
          letterSpacing="-0.02em"
          lineHeight="1.25"
          fontWeight="600"
          color={theme.text.primary}
        >
          {title}
        </Heading>
        {actions ? (
          <Box
            flexShrink={0}
            w={{ base: hasMobileSplit ? "auto" : "100%", md: "auto" }}
            display={{ base: hasMobileSplit ? "none" : "block", md: "block" }}
          >
            {actions}
          </Box>
        ) : null}
      </Flex>
      {belowTitle ? <Box mt={2}>{belowTitle}</Box> : null}
      {badges.length > 0 ? (
        <Flex
          align="center"
          gap={2}
          flexWrap="wrap"
          mt={belowTitle ? 2 : 0}
          mb={
            mobileBelowBadgesAction || subtitle || children
              ? 2
              : 0
          }
        >
          {badges.map((badge, index) => (
            <Box key={index}>{badge}</Box>
          ))}
        </Flex>
      ) : null}
      {mobileBelowBadgesAction ? (
        <Box display={{ base: "block", md: "none" }} w="100%">
          {mobileBelowBadgesAction}
        </Box>
      ) : null}
      {subtitle ? (
        typeof subtitle === "string" ? (
          <Text fontSize={{ base: "sm", md: "md" }} color={theme.text.tertiary}>
            {subtitle}
          </Text>
        ) : (
          subtitle
        )
      ) : null}
      {children}
    </Box>
  );
};
